/**
 * Source-video requirements for creating a digital_twin avatar.
 *
 * HeyGen's v3 docs don't publish a hard min/max but the consumer product +
 * historical v2 instant_avatar API recommend 30s–3min. Shorter clips are
 * usually accepted but training quality degrades, so we surface a warning
 * rather than blocking.
 */

export const DIGITAL_TWIN_VIDEO = {
    recommendedMinSeconds: 30,
    recommendedMaxSeconds: 180,
    hardMinSeconds: 5,
    hardMaxSeconds: 600,
    assetMaxBytes: 32 * 1024 * 1024,
    supportedMimeTypes: ["video/mp4", "video/quicktime", "video/webm"],
} as const;

export const PHOTO_AVATAR_IMAGE = {
    assetMaxBytes: 32 * 1024 * 1024,
    recommendedMinDimension: 512,
    supportedMimeTypes: ["image/png", "image/jpeg", "image/webp"],
} as const;

export const REFERENCE_IMAGE = {
    maxCount: 3,
    assetMaxBytes: 32 * 1024 * 1024,
    supportedMimeTypes: ["image/png", "image/jpeg", "image/webp"],
} as const;

export type VideoValidation =
    | { ok: true; warning?: string }
    | { ok: false; reason: string };

export function validateDigitalTwinVideo(
    sizeBytes: number,
    durationSec: number,
    mimeType: string
): VideoValidation {
    if (!DIGITAL_TWIN_VIDEO.supportedMimeTypes.includes(mimeType as never)) {
        return {
            ok: false,
            reason: `Unsupported video format: ${mimeType}. Use MP4, MOV, or WebM.`,
        };
    }
    if (sizeBytes > DIGITAL_TWIN_VIDEO.assetMaxBytes) {
        return {
            ok: false,
            reason: `Video exceeds the 32MB upload cap. Compress and try again.`,
        };
    }
    if (durationSec < DIGITAL_TWIN_VIDEO.hardMinSeconds) {
        return {
            ok: false,
            reason: `Video is too short (${durationSec.toFixed(1)}s). Minimum is ${DIGITAL_TWIN_VIDEO.hardMinSeconds}s.`,
        };
    }
    if (durationSec > DIGITAL_TWIN_VIDEO.hardMaxSeconds) {
        return {
            ok: false,
            reason: `Video is too long (${durationSec.toFixed(0)}s). Maximum is ${DIGITAL_TWIN_VIDEO.hardMaxSeconds}s.`,
        };
    }
    if (durationSec < DIGITAL_TWIN_VIDEO.recommendedMinSeconds) {
        return {
            ok: true,
            warning: `Videos shorter than ${DIGITAL_TWIN_VIDEO.recommendedMinSeconds}s may produce lower-quality avatars.`,
        };
    }
    return { ok: true };
}
